import { useEffect, useRef, useState } from "react";
import {
  AccessibilitySessionProvider,
  PinVerificationDialog,
  ResetReassuranceOverlay,
  useAccessibilitySession,
} from "@ascleo/frontdesk-shared";
import { Button } from "@ascleo/ui/components/ui/button";
import { AlertCircle, RotateCcw, ShieldCheck } from "lucide-react";
import type { DemoSession } from "../lib/gateway-client";
import { resetDemoSession, unlockDemoOperator } from "../lib/gateway-client";

interface DemoOperatorPageProps {
  readonly session: DemoSession;
  readonly defaultLanguage: string;
  readonly onSessionChange: (session: DemoSession) => void;
  readonly onReturnToDemo: () => void;
}

export function DemoOperatorPage({
  session,
  defaultLanguage,
  onSessionChange,
  onReturnToDemo,
}: DemoOperatorPageProps) {
  const [resetRedirectPending, setResetRedirectPending] = useState(false);
  const [pinDialogOpen, setPinDialogOpen] = useState(!session.operatorUnlocked);
  const verifiedThisVisitRef = useRef(session.operatorUnlocked);
  const [verifiedThisVisit, setVerifiedThisVisit] = useState(
    session.operatorUnlocked,
  );

  useEffect(function runResetRedirectPendingEffect() {
    if (resetRedirectPending) {
      return;
    }
    setPinDialogOpen(!session.operatorUnlocked);
  }, [resetRedirectPending, session.operatorUnlocked]);

  useEffect(function syncVerifiedThisVisit() {
    verifiedThisVisitRef.current = session.operatorUnlocked;
    setVerifiedThisVisit(session.operatorUnlocked);
  }, [session.operatorUnlocked]);

  const verifyPin = async (pin: string) => {
    try {
      const next = await unlockDemoOperator(pin);
      verifiedThisVisitRef.current = true;
      setVerifiedThisVisit(true);
      onSessionChange(next);
      return true;
    } catch {
      onReturnToDemo();
      return false;
    }
  };

  const handlePinDialogChange = (open: boolean) => {
    setPinDialogOpen(open);
    if (!open && !session.operatorUnlocked && !verifiedThisVisitRef.current) {
      onReturnToDemo();
    }
  };

  return (
    <AccessibilitySessionProvider
      adminDefaultLanguage={defaultLanguage}
      onResetComplete={onReturnToDemo}
    >
      <div className="frontdesk-web-shell">
        {session.operatorUnlocked || verifiedThisVisit ? (
          <DemoOperatorPanel
            session={session}
            onSessionChange={onSessionChange}
            onResetStarted={() => {
              setResetRedirectPending(true);
            }}
            onReturnToDemo={onReturnToDemo}
          />
        ) : (
          <DemoOperatorLockedState />
        )}
        <PinVerificationDialog
          open={pinDialogOpen}
          onOpenChange={handlePinDialogChange}
          onVerified={() => {
            verifiedThisVisitRef.current = true;
            setVerifiedThisVisit(true);
          }}
          verifyPin={verifyPin}
        />
      </div>
      <ResetReassuranceOverlay />
    </AccessibilitySessionProvider>
  );
}

function DemoOperatorPanel({
  session,
  onSessionChange,
  onResetStarted,
  onReturnToDemo,
}: {
  readonly session: DemoSession;
  readonly onSessionChange: (session: DemoSession) => void;
  readonly onResetStarted: () => void;
  readonly onReturnToDemo: () => void;
}) {
  const { triggerReset } = useAccessibilitySession();
  const [isResetting, setIsResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleReset = async () => {
    setIsResetting(true);
    setError(null);
    try {
      const next = await resetDemoSession();
      onSessionChange(next);
      onResetStarted();
      triggerReset("completion");
    } catch (cause) {
      if (
        cause instanceof Error &&
        /demo_(operator_unlock_required|session_required)/.test(cause.message)
      ) {
        onReturnToDemo();
        return;
      }
      setError(
        cause instanceof Error
          ? cause.message
          : "Demo reset could not be completed.",
      );
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="frontdesk-web-kiosk-shell">
      <div className="frontdesk-web-kiosk-content flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-2xl rounded-3xl border bg-white/95 p-8 shadow-xl backdrop-blur">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-4">
              <div className="flex size-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
                <ShieldCheck className="size-7" />
              </div>
              <div>
                <h1 className="text-3xl font-semibold text-slate-900">
                  FrontDesk+ Demo Operator
                </h1>
                <p className="text-sm text-slate-600">
                  Protected controls for resetting the public browser demo.
                </p>
              </div>
            </div>

            <div
              data-testid="frontdesk-demo-operator-diagnostics"
              className="grid gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-700 md:grid-cols-3"
            >
              <div>
                <p className="font-medium text-slate-500">Scenario</p>
                <p className="mt-1 font-semibold text-slate-900">
                  {session.scenarioId}
                </p>
              </div>
              <div>
                <p className="font-medium text-slate-500">Terminal</p>
                <p className="mt-1 font-semibold text-slate-900">
                  {session.terminalId}
                </p>
              </div>
              <div>
                <p className="font-medium text-slate-500">Last reset</p>
                <p className="mt-1 font-semibold text-slate-900">
                  {new Date(session.lastResetAt).toLocaleString("de-DE")}
                </p>
              </div>
            </div>

            {error ? (
              <div className="flex items-center gap-2 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                <AlertCircle className="size-4" />
                <span>{error}</span>
              </div>
            ) : null}

            <div className="flex flex-wrap gap-3">
              <Button
                data-testid="frontdesk-demo-operator-reset-btn"
                size="lg"
                onClick={handleReset}
                disabled={isResetting}
              >
                <RotateCcw className="mr-2 size-5" />
                {isResetting ? "Resetting demo..." : "Reset public demo"}
              </Button>
              <Button
                data-testid="frontdesk-demo-operator-return-btn"
                variant="outline"
                size="lg"
                onClick={onReturnToDemo}
                disabled={isResetting}
              >
                Return to /demo
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DemoOperatorLockedState() {
  return (
    <div className="frontdesk-web-kiosk-shell">
      <div className="frontdesk-web-kiosk-content flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-lg rounded-3xl border bg-white/95 p-8 text-center shadow-xl backdrop-blur">
          <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-slate-100 text-slate-700">
            <ShieldCheck className="size-7" />
          </div>
          <h1 className="mt-5 text-2xl font-semibold text-slate-900">
            Demo operator access
          </h1>
          <p className="mt-3 text-sm text-slate-600">
            Enter the operator PIN to access demo reset controls.
          </p>
        </div>
      </div>
    </div>
  );
}
